<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useResizeObserver } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMatchedSop } from 'dashboard/composables/useMatchedSop';
import { useOperatingProceduresStore } from 'dashboard/stores/operatingProcedures';
import { useSopRunsStore } from 'dashboard/stores/sopRuns';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import SopWalkthrough from './SopWalkthrough.vue';

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const operatingProceduresStore = useOperatingProceduresStore();
const sopRunsStore = useSopRunsStore();

const currentChat = computed(() => store.getters.getSelectedChat);
const { matchedSops } = useMatchedSop(currentChat);

// SOPs the walkthroughs have reached through reference nodes, keyed by the
// referenced SOP id. They get their own tab even when they don't match the
// conversation themselves.
const referrersByTarget = computed(() =>
  sopRunsStore.referrersByTarget(currentChat.value?.id)
);
const referencedSops = computed(() =>
  Object.keys(referrersByTarget.value)
    .map(id => operatingProceduresStore.getById(id))
    .filter(sop => sop && !matchedSops.value.some(m => m.id === sop.id))
);
const visibleSops = computed(() => [
  ...matchedSops.value,
  ...referencedSops.value,
]);

const referencedByFor = sop =>
  (referrersByTarget.value[sop.id] || [])
    .map(id => operatingProceduresStore.getById(id))
    .filter(Boolean)
    .map(referrer => ({ id: referrer.id, name: referrer.name }));

// A conversation can carry several contact reasons, each with its own SOP.
// The agent switches between them; each keeps its own persisted run.
const activeSopId = ref(null);
const activeSop = computed(
  () =>
    visibleSops.value.find(sop => sop.id === activeSopId.value) ||
    visibleSops.value[0] ||
    null
);

const sopTabs = computed(() =>
  visibleSops.value.map(sop => ({
    value: sop.id,
    label: sop.name,
  }))
);
const activeSopIndex = computed(() =>
  Math.max(
    visibleSops.value.findIndex(sop => sop.id === activeSop.value?.id),
    0
  )
);
const handleSopTabChange = tab => {
  activeSopId.value = tab.value;
};

watch(
  () => currentChat.value?.id,
  () => {
    activeSopId.value = null;
  }
);

// Slide the reason tabs horizontally (like the Shopee order status tabs)
// when they overflow the panel instead of wrapping.
const tabsScroller = ref(null);
const isOverflowing = ref(false);
const checkOverflow = () => {
  const el = tabsScroller.value;
  isOverflowing.value = el ? el.scrollWidth > el.clientWidth + 1 : false;
};
useResizeObserver(tabsScroller, checkOverflow);
watch(sopTabs, () => nextTick(checkOverflow));

const scrollTabs = direction => {
  tabsScroller.value?.scrollBy({ left: direction * 160, behavior: 'smooth' });
};

const shouldShowSopPanel = computed(
  () => uiSettings.value?.is_sop_panel_open && !!activeSop.value
);

// Every walkthrough resolves macros and canned responses from the same
// store lists, so load them once per panel open instead of once per SOP.
watch(
  shouldShowSopPanel,
  isOpen => {
    if (!isOpen) return;
    store.dispatch('macros/get');
    store.dispatch('getCannedResponse');
  },
  { immediate: true }
);

const closeSopPanel = () => {
  updateUISettings({ is_sop_panel_open: false });
};

// Jump straight to the SOP walkthrough once the agent tags a contact reason
// with a matching SOP.
const openSopIfMatched = ({ conversationId }) => {
  if (conversationId !== currentChat.value?.id || !matchedSops.value.length)
    return;
  updateUISettings({
    is_sop_panel_open: true,
    is_contact_sidebar_open: false,
    is_shared_files_panel_open: false,
    is_copilot_panel_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: false,
  });
};

onMounted(() => {
  operatingProceduresStore.get();
  emitter.on(BUS_EVENTS.CONTACT_REASONS_UPDATED, openSopIfMatched);
});
onUnmounted(() => {
  emitter.off(BUS_EVENTS.CONTACT_REASONS_UPDATED, openSopIfMatched);
});
</script>

<template>
  <div
    v-if="shouldShowSopPanel"
    class="ltr:border-l rtl:border-r border-n-weak h-full overflow-hidden z-10 w-[320px] min-w-[320px] 2xl:min-w-[360px] 2xl:w-[360px] flex flex-col bg-n-background"
  >
    <div class="flex flex-col h-full">
      <SidebarActionsHeader
        :title="$t('CONVERSATION.SIDEBAR.SOP')"
        @close="closeSopPanel"
      />
      <div
        v-if="visibleSops.length > 1"
        class="flex items-center gap-1 border-b border-n-strong px-2 py-2 shrink-0"
      >
        <Button
          v-if="isOverflowing"
          icon="i-lucide-chevron-left"
          ghost
          slate
          xs
          class="shrink-0"
          @click="scrollTabs(-1)"
        />
        <div
          ref="tabsScroller"
          class="flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <TabBar
            :tabs="sopTabs"
            :initial-active-tab="activeSopIndex"
            @tab-changed="handleSopTabChange"
          />
        </div>
        <Button
          v-if="isOverflowing"
          icon="i-lucide-chevron-right"
          ghost
          slate
          xs
          class="shrink-0"
          @click="scrollTabs(1)"
        />
      </div>
      <div class="flex-auto overflow-y-auto h-full w-full">
        <!-- Every visible SOP mounts at once so all runs preload in
             parallel; switching tabs just toggles visibility — no refetch,
             no flicker, in-memory state stays put. -->
        <SopWalkthrough
          v-for="sop in visibleSops"
          v-show="sop.id === activeSop.id"
          :key="`${currentChat.id}-${sop.id}`"
          class="m-3"
          :sop="sop"
          :conversation-id="currentChat.id"
          :referenced-by="referencedByFor(sop)"
          @open-sop="activeSopId = $event"
        />
      </div>
    </div>
  </div>
  <template v-else />
</template>
